import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Card,
  CardContent,
  FilterToolbarSearch,
  FormLabel,
  filterToolbarSearchGrowClassName,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import {
  FileText,
  Clock,
  AlertCircle,
  Calendar,
  User,
  Building,
  Shield,
  CheckCircle,
  XCircle,
  Loader2,
} from 'lucide-react';
import { getAuthQueue, getEligibilityChecks } from '../services/api';
import { formatDateForDisplay } from '../utils/dateUtils';
import { ReportLink } from '../components/reporting/ReportLink';
import { patientChartPath } from '../utils/navigation';
import type {
  IntakeResponse,
  EligibilityCheckResponse,
} from '../services/api/types.gen';
import PageLoader from '../components/PageLoader';
import PriorAuthDialog from '../components/PriorAuthDialog';

interface IntakeWithEligibility extends IntakeResponse {
  eligibilityCheck?: EligibilityCheckResponse;
}

const AuthManager: React.FC = () => {
  const [authQueue, setAuthQueue] = useState<IntakeWithEligibility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [authStatusFilter, setAuthStatusFilter] = useState<string>('all');
  const [filteredQueue, setFilteredQueue] = useState<IntakeWithEligibility[]>(
    []
  );
  const [selectedIntake, setSelectedIntake] =
    useState<IntakeWithEligibility | null>(null);
  const [showPriorAuthDialog, setShowPriorAuthDialog] = useState(false);

  const loadAuthQueue = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAuthQueue({});
      if (response && response.data) {
        const intakesWithPatients = response.data.filter(
          (intake: IntakeResponse) =>
            intake.patient_id && intake.insurance_verified
        );

        const intakesWithEligibility = await Promise.all(
          intakesWithPatients.map(async (intake: IntakeResponse) => {
            try {
              const eligibilityChecks = await getEligibilityChecks({
                query: { patient_id: intake.patient_id! },
              });
              // Get the most recent eligibility check
              const latestCheck =
                eligibilityChecks &&
                eligibilityChecks.data &&
                eligibilityChecks.data.length > 0
                  ? eligibilityChecks.data.sort(
                      (a: { created_at: string }, b: { created_at: string }) =>
                        new Date(b.created_at).getTime() -
                        new Date(a.created_at).getTime()
                    )[0]
                  : undefined;

              return {
                ...intake,
                eligibilityCheck: latestCheck,
              };
            } catch (err) {
              console.warn(
                `Failed to fetch eligibility for patient ${intake.patient_id}:`,
                err
              );
              return intake;
            }
          })
        );

        setAuthQueue(intakesWithEligibility);
      } else {
        console.warn('No auth queue data received from API');
        setAuthQueue([]);
      }
    } catch (err) {
      console.error('Error loading auth queue:', err);
      setError('Failed to load authorization queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAuthQueue();
  }, []);

  // Filter queue based on search term and auth status filter
  useEffect(() => {
    let filtered = authQueue;

    // Filter by auth status
    if (authStatusFilter !== 'all') {
      filtered = filtered.filter(intake => {
        const status = intake.authorization_status || 'PENDING';
        switch (authStatusFilter) {
          case 'approved':
            return status === 'APPROVED';
          case 'declined':
            return status === 'DENIED';
          case 'pending_auth':
            return status === 'PENDING' || !status;
          default:
            return true;
        }
      });
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(intake => {
        const patientName =
          `${intake.patient_first_name || ''} ${intake.patient_last_name || ''}`.toLowerCase();
        const intakeId = intake.id.toLowerCase();
        const primaryInsurance = (intake.primary_insurance || '').toLowerCase();

        return (
          patientName.includes(term) ||
          intakeId.includes(term) ||
          primaryInsurance.includes(term)
        );
      });
    }

    setFilteredQueue(filtered);
  }, [authQueue, searchTerm, authStatusFilter]);

  const formatDateTime = (date: string | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPatientName = (intake: IntakeWithEligibility) => {
    const firstName = intake.patient_first_name || '';
    const lastName = intake.patient_last_name || '';
    return firstName || lastName ? `${firstName} ${lastName}`.trim() : 'N/A';
  };

  const getAuthStatusBadge = (intake: IntakeWithEligibility) => {
    const status = intake.authorization_status || 'PENDING';

    switch (status) {
      case 'APPROVED':
        return (
          <Badge variant='outline' className='bg-green-100 text-green-800'>
            <Shield className='h-3 w-3 mr-1' />
            Approved
          </Badge>
        );
      case 'DENIED':
        return (
          <Badge variant='outline' className='bg-red-100 text-red-800'>
            <AlertCircle className='h-3 w-3 mr-1' />
            Declined
          </Badge>
        );
      default:
        return (
          <Badge variant='outline' className='bg-yellow-100 text-yellow-800'>
            <Clock className='h-3 w-3 mr-1' />
            Pending Auth
          </Badge>
        );
    }
  };

  const getEligibilityStatusBadge = (intake: IntakeWithEligibility) => {
    // If no eligibility check found
    if (!intake.eligibilityCheck) {
      return (
        <Badge variant='outline' className='bg-gray-100 text-gray-600'>
          <AlertCircle className='h-3 w-3 mr-1' />
          No Check
        </Badge>
      );
    }

    const status = intake.eligibilityCheck.status;

    switch (status) {
      case 'completed':
        return (
          <Badge variant='outline' className='bg-green-100 text-green-800'>
            <CheckCircle className='h-3 w-3 mr-1' />
            Verified
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant='outline' className='bg-red-100 text-red-800'>
            <XCircle className='h-3 w-3 mr-1' />
            Failed
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant='outline' className='bg-yellow-100 text-yellow-800'>
            <Clock className='h-3 w-3 mr-1' />
            Pending
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge variant='outline' className='bg-blue-100 text-blue-800'>
            <Loader2 className='h-3 w-3 mr-1' />
            In Progress
          </Badge>
        );
      case 'expired':
        return (
          <Badge variant='outline' className='bg-orange-100 text-orange-800'>
            <AlertCircle className='h-3 w-3 mr-1' />
            Expired
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge variant='outline' className='bg-gray-100 text-gray-600'>
            <XCircle className='h-3 w-3 mr-1' />
            Cancelled
          </Badge>
        );
      default:
        return (
          <Badge variant='outline' className='bg-gray-100 text-gray-600'>
            <AlertCircle className='h-3 w-3 mr-1' />
            Unknown
          </Badge>
        );
    }
  };

  const handleIntakeClick = (intake: IntakeWithEligibility) => {
    setSelectedIntake(intake);
    setShowPriorAuthDialog(true);
  };

  const handlePriorAuthUpdate = async () => {
    await loadAuthQueue();
  };

  if (loading) {
    return <PageLoader message='Loading authorization queue...' />;
  }

  return (
    <div className='space-y-6'>
      {/* Error Display */}
      {error && (
        <Card className='border-destructive bg-destructive/10'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 text-destructive'>
              <AlertCircle className='h-4 w-4' />
              <span className='font-medium'>
                Error loading authorization queue
              </span>
            </div>
            <p className='text-sm text-destructive/80 mt-1'>{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Search and Filters */}
      <div className='flex flex-wrap items-end gap-4'>
        <FilterToolbarSearch
          value={searchTerm}
          onChange={setSearchTerm}
          label='Search'
          placeholder='Search by patient name, ID, or insurance...'
          containerClassName={filterToolbarSearchGrowClassName}
          className='w-full'
        />
        <div className='w-48'>
          <FormLabel htmlFor='auth-status-filter' showTooltip={false}>
            Status
          </FormLabel>
          <Select value={authStatusFilter} onValueChange={setAuthStatusFilter}>
            <SelectTrigger id='auth-status-filter' className='w-48'>
              <SelectValue placeholder='Filter by Prior Auth Status' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All</SelectItem>
              <SelectItem value='approved'>Approved</SelectItem>
              <SelectItem value='declined'>Declined</SelectItem>
              <SelectItem value='pending_auth'>Pending Auth</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Queue Table */}
      {filteredQueue.length === 0 ? (
        <div className='text-center text-muted-foreground py-8'>
          <Shield className='h-12 w-12 mx-auto mb-4 text-gray-500' />
          <p>No intakes requiring authorization found</p>
          <p className='text-sm'>
            {searchTerm || authStatusFilter !== 'all'
              ? 'Try adjusting your search term or filter'
              : 'All intakes have been processed or do not require prior authorization'}
          </p>
        </div>
      ) : (
        <div className='rounded-md border'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Patient</TableHead>
                <TableHead>Auth Status</TableHead>
                <TableHead>Eligibility</TableHead>
                <TableHead>Intake Status</TableHead>
                <TableHead>Insurance</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredQueue.map(intake => (
                <TableRow
                  key={intake.id}
                  className='hover:bg-muted/50 cursor-pointer'
                  onClick={() => handleIntakeClick(intake)}
                >
                  <TableCell>
                    <div className='flex items-center gap-2'>
                      <User className='h-4 w-4 text-muted-foreground' />
                      <div>
                        <div className='font-medium'>
                          {intake.patient_id ? (
                            <ReportLink
                              to={patientChartPath(intake.patient_id)}
                            >
                              {getPatientName(intake)}
                            </ReportLink>
                          ) : (
                            getPatientName(intake)
                          )}
                        </div>
                        {intake.patient_dob && (
                          <div className='text-muted-foreground'>
                            DOB:{' '}
                            {formatDateForDisplay(intake.patient_dob ?? '') ||
                              'N/A'}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getAuthStatusBadge(intake)}</TableCell>
                  <TableCell>{getEligibilityStatusBadge(intake)}</TableCell>
                  <TableCell>
                    <Badge variant='secondary'>
                      <FileText className='h-3 w-3 mr-1' />
                      {intake.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className='flex items-center gap-2'>
                      <Building className='h-4 w-4 text-muted-foreground' />
                      <div>
                        <div className='font-medium'>
                          {intake.primary_insurance || 'N/A'}
                        </div>
                        {intake.primary_member_id && (
                          <div className='text-muted-foreground'>
                            ID: {intake.primary_member_id}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className='flex items-center gap-2'>
                      <Calendar className='h-4 w-4 text-muted-foreground' />
                      <span>{formatDateTime(intake.created_at)}</span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Prior Auth Dialog */}
      <PriorAuthDialog
        open={showPriorAuthDialog}
        onOpenChange={setShowPriorAuthDialog}
        selectedIntake={selectedIntake}
        onUpdate={handlePriorAuthUpdate}
      />
    </div>
  );
};

export default AuthManager;
